// ── SEO title guard ────────────────────────────────────────────
// Pre-rendered SEO pages (scrapers/generate_seo_pages.py) are built from this
// SPA template with a server-built <title> injected on top, and carry
// <meta name="seo-prerendered">. This module then runs and would overwrite it,
// so Google ends up indexing the SPA title instead of the SEO one.
// Swallow the FIRST title write when that marker is present (and consume the
// marker), so the SEO title survives the initial render. Later writes — an
// in-SPA navigation to another page — apply normally. No-op on the plain SPA.
function nbSetTitle(t){
  const m = document.querySelector('meta[name="seo-prerendered"]');
  if (m) { m.remove(); return; }
  document.title = t;
}

// producteur.js — fiche detaillee d'un producteur (ou groupe).
// Slug vient de l'URL /producteur/<slug>/ : la page reunit toutes les dates
// dont un producteur se ramene a cette adresse (groupe et filiales, memes
// noms avec ou sans accent), comme la page pre-rendue. Seuls comptent les
// liens OBSERVES (piege 7, producers_observed.json via event-fields.js).
//
// Toute la France : les dates de province (/data/province/events_upcoming.json,
// la copie que lit aussi artiste.js) passent par la meme annexe, qui couvre
// la province depuis scrapers/utils/observed_producers.py. Les dates de la
// ville choisie (js/villes.js) d'abord, puis les autres villes, une ville par
// groupe ; changer de ville dans l'en-tete reordonne la liste sur place. Les
// chiffres (salles, genres, saison) portent sur toute la France.
//
// Page pre-rendue (scrapers/generate_producer_pages.py) : le serveur a deja
// ecrit le haut de page, les chiffres cles et la liste des dates pour la
// ville par defaut (Paris), avec le meme balisage (scrapers/producteur_seo.py
// en est le portage). Toute modif d'un rendu ici se reporte la-bas ;
// tests/frontend/test_producer_parity.cjs compare les deux.
(function(){
'use strict';

const esc = s => (s==null?'':String(s)).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const $ = s => document.querySelector(s);

const DAYS = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];
// Ville par defaut (module des villes absent ou muet) : Paris, comme avant.
const PARIS_VILLE = {slug:'paris', nom:'Paris', a_nom:'à Paris', de_nom:'de Paris'};
// Au-delà, Paris : la page ne reste pas sur « Chargement… » (relecture du 18/10).
const VILLE_TIMEOUT_MS = 2000;
// Lignes de « Prochains concerts » affichees d'emblee, puis par paquet.
const PAGE = 30;
// byOf : sur la page d'un groupe, les structures du groupe qui produisent une
// date (memberNames) ; null sur la page d'un producteur seul.
const STATE = {ville: PARIS_VILLE, explicit: false, upcoming: [], shown: PAGE, byOf: null};
const MONTHS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Août','Sept','Oct','Nov','Déc'];
const MONTHS_LONG = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

const GENRE_COLOR = {
  'Pop & Variété':'#F59E32','Rock':'#E8562C','Rap & Hip-Hop':'#D4623F','RnB':'#B96F9A',
  'Électro':'#F59E32','Jazz / Soul / Funk':'#7BA9A6','Reggae & World':'#C9A44B',
  'Classique':'#5E9DB6','Métal / assimilés':'#E8562C','Folk / Indie':'#CFB065',
  'Spectacles':'#B96F9A','Humour':'#F59E32','Musique de Film':'#5E9DB6','Autre':'#9C9AA6'
};

function slugify(text){
  if(!text) return '';
  return String(text).toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^\x00-\x7f]/g,'')
    .replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
}
function getSlugFromUrl(){
  const m = window.location.pathname.match(/\/producteur\/([^\/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

// Salle d'une date : venues-data.js a Paris ; villes.json (js/villes.js) en
// province — nom affiche, couleur stable, jauge publique et ville.
function resolveVenue(e){
  if(!e || !e.venue) return null;
  if(e.__province){
    const api = window.LAgendaVilles;
    const s = api && api.salle(e.venue);
    if(!s) return null;
    const v = api.ville(s.ville);
    return {dbName: e.venue, display: s.nom || e.venue, hex: api.couleur(s.slug),
            capacity: s.capacite || 0, villeNom: v ? v.nom : (s.commune || '')};
  }
  if(!window.VENUE_BY_NAME) return null;
  return window.VENUE_BY_NAME[e.venue] || null;
}

// Ville d'une date (slug de data/villes.json) : le fichier de Paris ne porte
// que des salles de Paris ; une salle de province prend la sienne dans
// villes.json. null : salle de province hors de toute ville publiee.
function villeOf(e){
  if(!e.__province) return 'paris';
  const api = window.LAgendaVilles;
  const s = api && api.salle(e.venue);
  return (s && s.ville) || null;
}
function villeNom(slug){
  if(slug === STATE.ville.slug) return STATE.ville.nom;
  const api = window.LAgendaVilles;
  const v = slug && api && api.ville(slug);
  return v ? v.nom : (slug === 'paris' ? 'Paris' : 'Autres villes');
}

// Ville choisie dans l'en-tete (js/villes.js), ou null sans module des villes.
async function villeChoisie(){
  const api = window.LAgendaVilles;
  if(!api || !api.ready) return null;
  try {
    return await Promise.race([api.ready, new Promise(r => setTimeout(() => r(null), VILLE_TIMEOUT_MS))]);
  } catch(_){ return null; }
}

function buildGroupMap(groupsJson){
  const sub2parent = {};          // key lowercase → {parent, isGroup}
  const parent2subs = {};         // parent → [subsidiaries]
  const parent2aliases = {};      // parent → [aliases]
  const parent2formerly = {};     // parent → ancien nom (« Fimalac Entertainment »)
  if(!groupsJson || !groupsJson.groups) return {sub2parent, parent2subs, parent2aliases, parent2formerly};
  for(const parent of Object.keys(groupsJson.groups)){
    const g = groupsJson.groups[parent];
    // Un groupe a des filiales ; une entree qui n'a que des alias (autres
    // noms de la meme societe) n'en est pas un (is_group_entry au serveur).
    sub2parent[parent.toLowerCase()] = {parent, isGroup: (g.subsidiaries || []).length > 0};
    parent2subs[parent] = g.subsidiaries || [];
    parent2aliases[parent] = g.aliases || [];
    if(g.formerly) parent2formerly[parent] = g.formerly;
    (g.aliases || []).forEach(a => { sub2parent[a.toLowerCase()] = {parent, isGroup:false}; });
    (g.subsidiaries || []).forEach(s => { sub2parent[s.toLowerCase()] = {parent, isGroup:false}; });
  }
  return {sub2parent, parent2subs, parent2aliases, parent2formerly};
}

function normalizeProducer(raw, groupMap){
  if(!raw) return null;
  const key = raw.toLowerCase();
  if(groupMap.sub2parent[key]) return groupMap.sub2parent[key].parent;
  return raw;
}

// Page d'un GROUPE (`slug`) : les producteurs observes de la date qui s'y
// ramenent sans etre le groupe lui-meme (filiale ou autre nom : AUGURI
// PRODUCTIONS sur /producteur/aeg-presents-france/). Le nom tel qu'il est lu,
// un par orthographe, dans l'ordre de la date. Le groupe present sur la date
// n'efface pas sa filiale. producteur_seo.member_names.
function memberNames(e, slug, groupMap){
  const out = [];
  const seen = new Set();
  for(const raw of (Array.isArray(e.producer) ? e.producer : [])){
    if(isUnknown(raw)) continue;
    const name = String(raw).trim();
    const own = slugify(name);
    if(!own || own === slug || seen.has(own)) continue;
    const norm = normalizeProducer(name, groupMap);
    if(norm && slugify(norm) === slug){ seen.add(own); out.push(name); }
  }
  return out;
}
// « par A », « par A et B », « par A, B et C » (producteur_seo.by_line_html).
function byLineHtml(names){
  if(!names || !names.length) return '';
  const txt = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
  return `<span class="p-by">par ${esc(txt)}</span>`;
}

// Perimetre de la page, le meme que le serveur (producteur_seo.in_scope) :
// une date, une salle, un concert, ni sport ni conference.
function inScope(e){
  if(!e || !e.date || !e.venue) return false;
  if(e.category != null && e.category !== 'concert') return false;
  const F = window.LAgendaFields;
  return !(F && F.isNonConcert && F.isNonConcert(e.event_name));
}
const isUnknown = p => !p || /^\s*inconnu\s*$/i.test(p);

async function loadData(){
  const F = window.LAgendaFields;
  const [paris, province, groupsRes, observed] = await Promise.all([
    fetch('/data/events_upcoming.json').then(r => r.json()).catch(()=>[]),
    fetch('/data/province/events_upcoming.json').then(r => r.ok ? r.json() : null).catch(()=>null),
    F.loadProducerGroups(),
    F.loadObservedProducers()
  ]);
  // `producer` brut = liens inferes compris : 25 des 39 dates de Base
  // Productions ne tenaient qu'a eux. On ne garde que les observes (annexe
  // absente, V2 : brut).
  const events = Array.isArray(paris) ? paris : [];
  F.keepObservedProducers(events, observed);
  // Province : seulement avec l'annexe. Son `producer` brut mele lui aussi les
  // liens inferes (plus nombreux que les observes) : sans annexe, aucune date
  // de province plutot qu'une date devinee. Une annexe qui ne couvre pas la
  // province (base province absente au build) les vide : elles ne comptent
  // alors pour aucun producteur. Un id deja servi par Paris : la date de Paris.
  if(observed && Array.isArray(province)){
    const seen = new Set(events.map(e => e && e.id != null ? String(e.id) : null));
    const prov = province.filter(e => e && !(e.id != null && seen.has(String(e.id))));
    F.keepObservedProducers(prov, observed);
    for(const e of prov){ e.__province = true; events.push(e); }
  }
  return {events, groups: buildGroupMap(groupsRes), observed};
}

function aggregate(events, slug, groupMap){
  // Tous les events dont au moins 1 producteur se ramene a l'adresse `slug`
  // (une page par adresse : « LES DERNIERS COUCHES » et « … COUCHÉS » sont la
  // meme maison, comme sur la page pre-rendue).
  const matched = [];
  for(const e of events){
    if(!inScope(e)) continue;
    const prods = Array.isArray(e.producer) ? e.producer : [];
    let hit = false;
    for(const raw of prods){
      if(isUnknown(raw)) continue;
      const norm = normalizeProducer(raw, groupMap);
      if(norm && slugify(norm) === slug){ hit = true; break; }
    }
    if(hit) matched.push(e);
  }
  return matched;
}

function computeStats(matched){
  const now = new Date(); now.setHours(0,0,0,0);
  const stats = {
    total: matched.length,
    upcoming: 0,
    past: 0,
    soldOut: 0,
    nextDate: null,
    nextEvent: null,
    byVenue: new Map(),       // dbName → {count, soldOut, ticketsEst}
    byGenre: new Map(),       // genre → {count, soldOut}
    byMonth: new Array(12).fill(0),
    venuesSet: new Set(),
    genresSet: new Set(),
    ticketsEstTotal: 0,
    provinceCount: 0,         // dates de province (fichier province)
    upcomingList: [],
    allList: []
  };
  for(const e of matched){
    if(e.__province) stats.provinceCount += 1;
    const d = new Date(e.date + 'T00:00:00');
    const isFuture = d >= now;
    if(isFuture){ stats.upcoming += 1; stats.upcomingList.push(e); }
    else stats.past += 1;
    const isSold = e.sold_out === 1 || e.sold_out === true || e.sale_status === 'sold_out';
    if(isSold) stats.soldOut += 1;
    if(isFuture && (!stats.nextDate || d < stats.nextDate)){
      stats.nextDate = d; stats.nextEvent = e;
    }
    // Venue breakdown
    const v = resolveVenue(e);
    const venueKey = v ? v.dbName : e.venue;
    if(!stats.byVenue.has(venueKey)) stats.byVenue.set(venueKey, {count:0, soldOut:0, ticketsEst:0, venue:v, name:venueKey});
    const vAcc = stats.byVenue.get(venueKey);
    vAcc.count += 1;
    if(isSold) vAcc.soldOut += 1;
    if(v && v.capacity) vAcc.ticketsEst += v.capacity;
    stats.venuesSet.add(venueKey);
    // Genre breakdown
    if(e.genre){
      if(!stats.byGenre.has(e.genre)) stats.byGenre.set(e.genre, {count:0, soldOut:0});
      const gAcc = stats.byGenre.get(e.genre);
      gAcc.count += 1;
      if(isSold) gAcc.soldOut += 1;
      stats.genresSet.add(e.genre);
    }
    // Month
    stats.byMonth[d.getMonth()] += 1;
    // Tickets estimate (all events, not just upcoming)
    if(v && v.capacity) stats.ticketsEstTotal += v.capacity;
    stats.allList.push(e);
  }
  stats.upcomingList.sort((a,b) => a.date.localeCompare(b.date));
  return stats;
}

// ===== RENDER =====

function renderHero(name, isGroup, groupMap){
  $('#hero-name').innerHTML = esc(name);
  $('#crumb-name').textContent = name;
  const grpEl = $('#hero-group');
  if(isGroup){ grpEl.style.display = 'inline-block'; }

  const subsidiaries = groupMap.parent2subs[name] || [];
  // Ancien nom, puis les alias qui n'en sont pas un morceau (FIMALAC dans
  // « Fimalac Entertainment ») — producteur_seo.hero_subsidiaries_html.
  const formerly = (groupMap.parent2formerly || {})[name] || '';
  const aliases = (groupMap.parent2aliases[name] || [])
    .filter(a => !(formerly && formerly.toLowerCase().includes(a.toLowerCase())));
  const subEl = $('#hero-subsidiaries');
  const pieces = [];
  if(formerly) pieces.push(`<b>Anciennement</b> ${esc(formerly)}`);
  if(aliases.length) pieces.push(`<b>Alias</b> ${aliases.map(esc).join(' · ')}`);
  if(subsidiaries.length) pieces.push(`<b>Maisons</b> ${subsidiaries.map(s => `<span>${esc(s)}</span>`).join('')}`);
  if(pieces.length){ subEl.innerHTML = pieces.join(' &nbsp; '); subEl.style.display = 'block'; }
}

// Ou sont les dates a venir : « en France · 24 villes », « à Paris ».
// producteur_seo.where_label.
function whereLabel(list){
  const villes = new Set(list.map(villeOf));
  if(villes.size > 1) return `en France · <b>${villes.size}</b> villes`;
  if(villes.size === 1){
    const slug = Array.from(villes)[0];
    const api = window.LAgendaVilles;
    const v = slug === STATE.ville.slug ? STATE.ville : (slug && api && api.ville(slug)) || (slug === 'paris' ? PARIS_VILLE : null);
    return v ? esc(v.a_nom || `à ${v.nom}`) : 'en France';
  }
  return 'aucune date à venir';
}

function renderKPIs(stats, isGroup){
  const topVenue = Array.from(stats.byVenue.values()).sort((a,b)=>b.count-a.count)[0];
  // « À venir » : le meme nombre que le haut de page (« N concerts »), rendu
  // au serveur sur les memes dates a venir de toute la France.
  const soldPct = stats.total ? Math.round(100 * stats.soldOut / stats.total) : 0;
  const nextLbl = stats.nextDate
    ? `${DAYS[stats.nextDate.getDay()]} ${stats.nextDate.getDate()} ${MONTHS_SHORT[stats.nextDate.getMonth()]}`
    : '—';
  const nextVenue = stats.nextEvent ? resolveVenue(stats.nextEvent) : null;
  const nextWhere = nextVenue
    ? nextVenue.display + (nextVenue.villeNom ? ` · ${nextVenue.villeNom}` : '')
    : (stats.nextEvent && stats.nextEvent.venue) || '';
  const nextSub = stats.nextEvent
    ? `<b>${esc(stats.nextEvent.event_name || '').slice(0,32)}</b> · ${esc(nextWhere)}`
    : 'Pas de date programmée';

  $('#p-kpi').innerHTML = `
    <div class="cell"><span class="k">À venir</span><div class="v">${stats.upcoming}</div><div class="d">${whereLabel(stats.upcomingList)}</div></div>
    <div class="cell"><span class="k">Sold out</span><div class="v"><em>${soldPct}</em><span class="unit">%</span></div><div class="d"><b>${stats.soldOut}</b> soirs complets</div></div>
    <div class="cell"><span class="k">Salles touchées</span><div class="v">${stats.venuesSet.size}</div><div class="d"><b>${topVenue ? esc(topVenue.venue ? topVenue.venue.display : topVenue.name) : '—'}</b> · ${topVenue ? topVenue.count : 0} dates</div></div>
    <div class="cell"><span class="k">Genres</span><div class="v">${stats.genresSet.size}</div><div class="d">${Array.from(stats.genresSet).slice(0,2).map(esc).join(' · ') || '—'}</div></div>
    <div class="cell"><span class="k">Prochain</span><div class="v small">${nextLbl}</div><div class="d">${nextSub}</div></div>
  `;
}

// Fiche /concert/ d'une date : table posee par le pre-rendu
// (window.__LAGENDA_SLUGS, comme artiste.js et salle.js), jamais recalculee.
// Page pre-rendue (table presente) : une date absente de la table n'a pas de
// fiche publiee — elle mene a sa billetterie, comme au serveur. Coquille SPA
// (sans table) : une date de Paris garde l'adresse que concert.js sait relire
// (titre + salle, d'apres le fichier de Paris) ; une date de province n'a de
// fiche que dans une ville ouverte, et concert.js ne la connait pas — elle
// mene a sa billetterie, jamais a une adresse fabriquee qui finirait en 404.
function eventLink(e){
  const table = window.__LAGENDA_SLUGS;
  const slug = (table || {})[String(e.id)];
  if(slug) return {href: `/concert/${encodeURIComponent(slug)}/`, ext: false};
  if(!table && !e.__province) return {href: `/concert/${encodeURIComponent(slugify(e.event_name) + '-' + slugify(e.venue))}/`, ext: false};
  const t = window.LAgendaFields.ticketHref(e);
  return t && /^https?:\/\//i.test(t) ? {href: t, ext: true} : null;
}

function eventRowHtml(e){
  const d = new Date(e.date + 'T00:00:00');
  const v = resolveVenue(e);
  const color = v ? v.hex : '#9C9AA6';
  const isSold = e.sold_out === 1 || e.sold_out === true || e.sale_status === 'sold_out';
  const statusCls = isSold ? 's sold' : 's dispo';
  // Prix : les bornes normalisees, comme les cartes (event-fields.js
  // parsePrice) — le texte brut disait « 69 € » pour 39,50-85 € (2026-10-20).
  // Miroir : producteur_seo.event_row_html.
  const priceLbl = window.LAgendaFields.parsePrice(e.price, e).label;
  const statusLbl = isSold ? 'Sold' : (priceLbl && priceLbl !== '—' ? priceLbl : 'Dispo');
  const link = eventLink(e);
  const cells = `
        <div class="d"><span class="day">${DAYS[d.getDay()]}</span> ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}</div>
        <div class="nm">${esc(e.event_name || '—')}${byLineHtml(STATE.byOf ? STATE.byOf(e) : [])}</div>
        <div class="v"><span class="sw" style="background:${color}"></span>${esc(v ? v.display : e.venue)}</div>
        <div class="g">${esc(e.genre || '—')}</div>
        <div class="${statusCls}">${esc(statusLbl)}${link && link.ext ? ' ↗<span class="p-vh"> billetterie, nouvel onglet</span>' : ''}</div>`;
  if(!link) return `
      <div class="p-evt p-evt-nolink">${cells}
      </div>`;
  if(link.ext){
    const F = window.LAgendaFields;
    return `
      <a class="p-evt" href="${esc(link.href)}" target="_blank" rel="noopener" title="${esc((e.event_name || '') + ' — billetterie')}" data-id="${esc(e.id)}" data-platform="${esc(F.ctaPlatform(e))}" data-venue="${esc(e.venue)}" data-genre="${esc(e.genre || '')}">${cells}
      </a>`;
  }
  return `
      <a class="p-evt" href="${esc(link.href)}" title="${esc(e.event_name || '')}">${cells}
      </a>`;
}

// Les dates de la ville choisie d'abord, puis les autres villes : une ville par
// groupe, la plus fournie d'abord. [{slug, nom, list}]. Une ville CHOISIE
// figure toujours en tete, meme sans date (« aucune date ») ; Paris par
// defaut (personne n'a choisi, robots compris) n'a pas de groupe vide : c'est
// le rendu neutre du serveur (producteur_seo.groups_by_ville).
function groupsByVille(list){
  const here = STATE.ville.slug;
  const by = new Map();
  for(const e of list){
    const slug = villeOf(e);
    if(!by.has(slug)) by.set(slug, []);
    by.get(slug).push(e);
  }
  const others = Array.from(by.entries()).filter(([slug]) => slug !== here)
    .map(([slug, l]) => ({slug, nom: villeNom(slug), list: l}))
    .sort((a,b) => (b.list.length - a.list.length) || a.nom.localeCompare(b.nom, 'fr'));
  const head = by.get(here) || [];
  if(!head.length && !STATE.explicit) return others;
  return [{slug: here, nom: STATE.ville.nom, list: head}, ...others];
}

function renderUpcoming(){
  const el = $('#p-upcoming');
  const sub = $('#p-upcoming-sub');
  const all = STATE.upcoming;
  if(!all.length){
    sub.innerHTML = '<b>0</b> dates programmées';
    el.innerHTML = '<div class="p-empty-sect">Aucune date à venir dans la DB.</div>';
    return;
  }
  const groups = groupsByVille(all);
  const villes = groups.filter(g => g.list.length).length;
  // Toutes les dates dans la ville choisie : une seule liste, comme avant.
  const single = groups.length === 1;
  sub.innerHTML = `<b>${all.length}</b> date${all.length>1?'s':''} programmée${all.length>1?'s':''}`
    + (villes > 1 ? ` en France · <b>${villes}</b> villes` : '');
  const rows = [];
  let n = 0;
  for(const g of groups){
    if(n >= STATE.shown) break;
    if(!single){
      const count = g.list.length
        ? `${g.list.length} date${g.list.length>1?'s':''}`
        : 'aucune date pour l’instant';
      rows.push(`<h4 class="p-grp">${esc(g.nom)} <span>${count}</span></h4>`);
    }
    for(const e of g.list){
      if(n >= STATE.shown) break;
      rows.push(eventRowHtml(e));
      n++;
    }
  }
  const rest = all.length - n;
  if(rest > 0){
    rows.push(`<div class="a-more"><button type="button" id="p-upcoming-more">Voir ${Math.min(PAGE, rest)} date${Math.min(PAGE, rest)>1?'s':''} de plus →</button></div>`);
  }
  el.innerHTML = rows.join('');
  const more = $('#p-upcoming-more');
  if(more) more.onclick = () => { STATE.shown += PAGE; renderUpcoming(); };
}

function renderVenues(stats){
  const el = $('#p-venues');
  const arr = Array.from(stats.byVenue.values()).sort((a,b)=>b.count-a.count);
  if(!arr.length){
    el.innerHTML = '<div class="p-empty-sect">Pas de répartition par salle.</div>';
    return;
  }
  const max = Math.max(...arr.map(v=>v.count));
  $('#p-venues-sub').innerHTML = `<b>${arr.length}</b> salles programmées · trié par volume`;
  el.innerHTML = arr.map(v => {
    const pctW = (v.count / max) * 100;
    const outside = pctW < 18;
    const color = v.venue ? v.venue.hex : 'var(--halo)';
    const soldPct = v.count ? Math.round(100 * v.soldOut / v.count) : 0;
    const cap = v.venue ? v.venue.capacity : 0;
    const capLbl = (v.venue && v.venue.villeNom ? `${esc(v.venue.villeNom)} · ` : '')
      + (cap ? `${cap.toLocaleString('fr-FR')} pl.` : 'capacité ?');
    return `
      <div class="p-bar-row">
        <div class="nm"><span class="sw" style="background:${color}"></span>${esc(v.venue ? v.venue.display : v.name)}<small>${capLbl}</small></div>
        <div class="track">
          <div class="bar" style="width:${pctW}%;background:${color}"></div>
          <span class="val ${outside?'out':''}" style="left:${outside?pctW:0}%">${v.count} date${v.count>1?'s':''}</span>
        </div>
        <div class="num">${v.count}</div>
        <div class="sold"><b>${soldPct}</b>%</div>
      </div>`;
  }).join('');

  // Tickets estimate
  const ticketsEl = $('#p-tickets');
  const total = stats.ticketsEstTotal;
  const upcomingTickets = Array.from(stats.byVenue.values()).reduce((s,v) => {
    const upc = stats.upcomingList.filter(e => resolveVenue(e)?.dbName === v.name).length;
    return s + ((v.venue && v.venue.capacity) ? v.venue.capacity * upc : 0);
  }, 0);
  if(total > 0){
    ticketsEl.innerHTML = `
      <span class="big">${total.toLocaleString('fr-FR')}</span>
      <span class="lbl">billets estimés produits (capacité × dates, toutes salles confondues) · <b>${upcomingTickets.toLocaleString('fr-FR')}</b> sur les dates à venir uniquement · <b>indicatif</b>, ne tient pas compte du taux de remplissage réel</span>
    `;
  } else {
    ticketsEl.style.display = 'none';
  }
}

function renderSeason(stats){
  const el = $('#p-season');
  const max = Math.max(1, ...stats.byMonth);
  const peak = stats.byMonth.indexOf(max);
  el.innerHTML = stats.byMonth.map((n, i) => {
    const h = max > 0 ? Math.max(2, Math.round((n/max) * 150)) : 2;
    return `
      <div class="col ${i===peak && n>0?'peak':''}" title="${MONTHS_LONG[i]} : ${n} date${n>1?'s':''}">
        <div class="bar-wrap"><div class="bar" style="height:${h}px">${n>0 ? `<span class="n">${n}</span>` : ''}</div></div>
        <div class="lbl">${MONTHS_SHORT[i]}</div>
      </div>`;
  }).join('');
  const peakLbl = stats.byMonth[peak] > 0 ? `${MONTHS_LONG[peak]} (${stats.byMonth[peak]} dates)` : '—';
  $('#p-season-sub').innerHTML = `Pic : <b>${peakLbl}</b> · distribution DB complète`;
}

function renderGenres(stats){
  const el = $('#p-genres');
  const arr = Array.from(stats.byGenre.entries()).map(([name, d]) => ({name, ...d})).sort((a,b)=>b.count-a.count);
  if(!arr.length){
    el.innerHTML = '<div class="p-empty-sect">Pas de genre documenté.</div>';
    return;
  }
  const max = Math.max(...arr.map(g=>g.count));
  const total = arr.reduce((s,g)=>s+g.count, 0);
  $('#p-genres-sub').innerHTML = `<b>${arr.length}</b> genres · ${total} évènements catégorisés`;
  el.innerHTML = arr.map(g => {
    const pctW = (g.count / max) * 100;
    const outside = pctW < 18;
    const share = Math.round(100 * g.count / total);
    const color = GENRE_COLOR[g.name] || 'var(--halo)';
    const soldPct = g.count ? Math.round(100 * g.soldOut / g.count) : 0;
    return `
      <div class="p-bar-row">
        <div class="nm"><span class="sw" style="background:${color}"></span>${esc(g.name)}</div>
        <div class="track">
          <div class="bar" style="width:${pctW}%;background:${color}"></div>
          <span class="val ${outside?'out':''}" style="left:${outside?pctW:0}%">${share}%</span>
        </div>
        <div class="num">${g.count}</div>
        <div class="sold"><b>${soldPct}</b>%</div>
      </div>`;
  }).join('');
}

// Resolution du producteur cible depuis le slug URL. Cherche d'abord dans les groupes
// (parent), puis dans tous les noms de producteurs uniques trouves dans les events.
function resolveTargetName(slug, events, groupMap){
  // 1) Match sur les groupes (parent) — groupe s'il a des filiales
  for(const parent of Object.keys(groupMap.parent2subs)){
    if(slugify(parent) === slug){
      return {name: parent, isGroup: groupMap.parent2subs[parent].length > 0};
    }
  }
  // 2) Match sur les producteurs individuels (inclut subsidiaries)
  const seen = new Set();
  for(const e of events){
    for(const p of (e.producer || [])){
      if(seen.has(p)) continue;
      seen.add(p);
      if(slugify(p) === slug){
        // Si c'est une subsidiary, renvoyer le parent. Sinon le raw.
        const key = p.toLowerCase();
        if(groupMap.sub2parent[key] && groupMap.sub2parent[key].parent.toLowerCase() !== key){
          const parent = groupMap.sub2parent[key].parent;
          return {name: parent, isGroup: (groupMap.parent2subs[parent] || []).length > 0};
        }
        return {name: p, isGroup: false};
      }
    }
  }
  return null;
}

// Adresse d'une filiale ou d'un autre nom d'un groupe (/producteur/auguri-productions/,
// /producteur/ts3/) : aucune page n'y est publiee, ses dates sont sur celle de
// son groupe. Renvoie l'adresse du groupe, ou null (groupe lui-meme,
// producteur seul, nom inconnu).
function groupSlugOf(slug, groupsJson){
  const groups = groupsJson && groupsJson.groups;
  if(!slug || !groups || typeof groups !== 'object') return null;
  const parents = Object.keys(groups);
  if(parents.some(p => slugify(p) === slug)) return null;
  for(const parent of parents){
    const g = groups[parent] || {};
    for(const n of [...(g.subsidiaries || []), ...(g.aliases || [])]){
      if(n && slugify(n) === slug) return slugify(parent);
    }
  }
  return null;
}

// Ville que la personne a choisie (memorisee) : Paris par defaut n'en est pas une.
function villeExplicite(ville){
  const api = window.LAgendaVilles;
  const c = api && typeof api.choisie === 'function' ? api.choisie() : null;
  return !!(ville && c && c.slug === ville.slug);
}

async function boot(){
  const slug = getSlugFromUrl();
  const prerendered = !!document.querySelector('meta[name="seo-prerendered"]');
  // Nom ecrit par le serveur : celui de la page (une adresse peut reunir
  // plusieurs orthographes, le serveur a choisi). Le H1 ne change pas.
  const serverName = prerendered ? ($('#hero-name').textContent || '').trim() : '';
  if(!slug){ $('#p-main').innerHTML = '<section class="p-hero"><h1>Producteur introuvable</h1><div class="sub">L\'URL ne contient pas d\'identifiant.</div></section>'; return; }

  // La ville choisie (en-tete) decide de l'ordre des dates : attendue avec
  // les donnees, Paris si le module des villes ne repond pas.
  const ready = Promise.all([loadData(), villeChoisie()]);

  // Filiale ou autre nom d'un groupe : la page du groupe, plutot que le groupe
  // affiche avec 0 date (les dates ne se ramenent qu'a l'adresse du groupe).
  // Jamais sur une page pre-rendue : le serveur n'en ecrit qu'aux adresses
  // de groupe ou de producteur seul.
  if(!prerendered){
    const target = groupSlugOf(slug, await window.LAgendaFields.loadProducerGroups());
    if(target && target !== slug){
      window.location.replace(`/producteur/${encodeURIComponent(target)}/${window.location.search || ''}${window.location.hash || ''}`);
      return;
    }
  }

  const [{events, groups, observed}, ville] = await ready;
  // Sans annexe, `producer` reste brut (inferes compris) : une page
  // pre-rendue garde alors son rendu serveur, deja restreint aux observes.
  if(!observed && prerendered) return;
  STATE.ville = ville || PARIS_VILLE;
  STATE.explicit = villeExplicite(ville);
  const found = resolveTargetName(slug, events, groups);
  const target = serverName
    ? {name: serverName, isGroup: (groups.parent2subs[serverName] || []).length > 0}
    : found;
  if(!target){
    $('#p-main').innerHTML = `<section class="p-hero"><h1>Producteur introuvable</h1><div class="sub">Aucun producteur ne correspond à ce slug dans la DB. <a href="/producteurs">Retour à la liste</a>.</div></section>`;
    return;
  }

  renderHero(target.name, target.isGroup, groups);
  STATE.byOf = target.isGroup ? (e => memberNames(e, slug, groups)) : null;
  const matched = aggregate(events, slug, groups);
  const stats = computeStats(matched);
  renderKPIs(stats, target.isGroup);
  STATE.upcoming = stats.upcomingList;
  renderUpcoming();
  renderVenues(stats);
  renderSeason(stats);
  renderGenres(stats);
  $('#foot-stats').textContent = `${stats.total} dates connues · ${stats.upcoming} à venir · ${stats.venuesSet.size} salles`;
  nbSetTitle(`${target.name} — L'Agenda`);

  // Changer de ville dans l'en-tete reordonne les dates sur place (js/villes.js
  // ne navigue plus vers l'agenda des qu'une page s'abonne). Le reste de la
  // page porte sur toute la France : il ne bouge pas.
  if(ville && window.LAgendaVilles && window.LAgendaVilles.onChange){
    window.LAgendaVilles.onChange(v => {
      if(!v) return;
      STATE.ville = v;
      STATE.explicit = true;
      STATE.shown = PAGE;
      renderUpcoming();
    });
  }
}

document.addEventListener('DOMContentLoaded', boot);
})();
